export type Locale = "de" | "en";

export const SITE_URL = "https://calvarius.com";

export const services = [
  { slug: "performance-marketing", enSlug: "performance-marketing", de: "Performance Marketing", en: "Performance Marketing" },
  { slug: "seo-ai-seo", enSlug: "seo-ai-seo", de: "SEO & AI SEO", en: "SEO & AI SEO" },
  { slug: "conversion-web-optimierung", enSlug: "conversion-optimization", de: "Conversion- & Web-Optimierung", en: "Conversion & Web Optimization" },
  { slug: "tracking-datenbasis", enSlug: "tracking-data-foundation", de: "Tracking & Datenbasis", en: "Tracking & Data Foundation" },
  { slug: "strategie-koordination", enSlug: "strategic-coordination", de: "Strategie & Koordination", en: "Strategic Coordination" },
  { slug: "entwicklung-digitaler-dienste", enSlug: "digital-development", de: "Entwicklung digitaler Dienste", en: "Digital Development" },
  { slug: "conversion-rate-optimierung", enSlug: "conversion-rate-optimization", de: "Conversion-Rate-Optimierung", en: "Conversion Rate Optimization" },
  { slug: "website-entwicklung", enSlug: "website-development", de: "Website-Entwicklung", en: "Website Development" },
  { slug: "onlineshop-entwicklung", enSlug: "ecommerce-development", de: "Onlineshop-Entwicklung", en: "E-Commerce Development" },
] as const;

/**
 * Map of canonical "page key" → DE path → EN path.
 * Used by the locale switcher and sitemap.
 */
export const pathMap: Record<string, { de: string; en: string }> = {
  home: { de: "/", en: "/en" },
  services: { de: "/leistungen", en: "/en/services" },
  about: { de: "/ueber-uns", en: "/en/about" },
  contact: { de: "/kontakt", en: "/en/contact" },
  imprint: { de: "/impressum", en: "/en/imprint" },
  privacy: { de: "/datenschutzerklaerung", en: "/en/privacy-policy" },
  accessibility: { de: "/barrierefreiheit", en: "/en/accessibility" },
  tools: { de: "/leistungen/tools", en: "/en/services/tools" },
  "tools-hub": { de: "/tools", en: "/en/tools" },
  "lighthouse-check": { de: "/tools/lighthouse-check", en: "/en/tools/lighthouse-check" },
  "profitability-calculator": { de: "/tools/wirtschaftlichkeitsrechner", en: "/en/tools/profitability-calculator" },
  "performance-marketing-checklist": { de: "/tools/performance-marketing-checkliste", en: "/en/tools/performance-marketing-checklist" },
  delta: { de: "/leistungen/delta", en: "/en/services/delta" },
  picazzoo: { de: "/leistungen/picazzoo", en: "/en/services/picazzoo" },
  gonzalads: { de: "/leistungen/gonzalads", en: "/en/services/gonzalads" },
  "pm:google-ads": { de: "/leistungen/performance-marketing/google-ads", en: "/en/services/performance-marketing/google-ads" },
  "pm:meta-ads": { de: "/leistungen/performance-marketing/meta-ads", en: "/en/services/performance-marketing/meta-ads" },
  "pm:meta-ads-recruiting": { de: "/leistungen/performance-marketing/meta-ads/recruiting", en: "/en/services/performance-marketing/meta-ads/recruiting" },
  "pm:linkedin-ads": { de: "/leistungen/performance-marketing/linkedin-ads", en: "/en/services/performance-marketing/linkedin-ads" },
  "pm:tiktok-ads": { de: "/leistungen/performance-marketing/tiktok-ads", en: "/en/services/performance-marketing/tiktok-ads" },
  "pm:microsoft-ads": { de: "/leistungen/performance-marketing/microsoft-ads", en: "/en/services/performance-marketing/microsoft-ads" },
  "pm:youtube-demand-gen": { de: "/leistungen/performance-marketing/youtube-demand-gen", en: "/en/services/performance-marketing/youtube-demand-gen" },
  "pm:affiliate-marketing": { de: "/leistungen/performance-marketing/affiliate-marketing", en: "/en/services/performance-marketing/affiliate-marketing" },
  "pm:social-media-werbung": { de: "/leistungen/performance-marketing/social-media-werbung", en: "/en/services/performance-marketing/social-media-advertising" },
  regions: { de: "/regionen", en: "/en/regions" },
  "region:wuerzburg": { de: "/regionen/wuerzburg", en: "/en/regions/wuerzburg" },
  "region:frankfurt": { de: "/regionen/frankfurt", en: "/en/regions/frankfurt" },
  "region:aschaffenburg": { de: "/regionen/aschaffenburg", en: "/en/regions/aschaffenburg" },
  "region:schweinfurt": { de: "/regionen/schweinfurt", en: "/en/regions/schweinfurt" },
  "region:nuernberg": { de: "/regionen/nuernberg", en: "/en/regions/nuernberg" },
  blog: { de: "/blog", en: "/en/blog" },
  "blog:lovable-seo-tanstack": { de: "/blog/lovable-seo-tanstack", en: "/en/blog/lovable-seo-tanstack" },
  "blog:google-ads-budget-pacing-juni-2026": { de: "/blog/google-ads-budget-pacing-juni-2026", en: "/en/blog/google-ads-budget-pacing-june-2026" },
  "blog:google-ai-suche-updates-mai-2026": { de: "/blog/google-ai-suche-updates-mai-2026", en: "/en/blog/google-ai-suche-updates-mai-2026" },
  "blog:faq-schema-jsonld-seo": { de: "/blog/faq-schema-jsonld-seo", en: "/en/blog/faq-schema-jsonld-seo" },
  "blog:lovable-seo-update-mai-2026": { de: "/blog/lovable-seo-update-mai-2026", en: "/en/blog/lovable-seo-update-may-2026" },
  glossary: { de: "/glossar", en: "/en/glossary" },
  "service:social-media-marketing": { de: "/leistungen/social-media-marketing", en: "/en/services/social-media-marketing" },
  "service:geo-generative-engine-optimization": { de: "/leistungen/geo-generative-engine-optimization", en: "/en/services/geo-generative-engine-optimization" },
  ...Object.fromEntries(
    services.map((s) => [
      `service:${s.slug}`,
      { de: `/leistungen/${s.slug}`, en: `/en/services/${s.enSlug}` },
    ]),
  ),
  "service:shopify-agentur": {
    de: "/leistungen/onlineshop-entwicklung/shopify-agentur",
    en: "/en/services/ecommerce-development/shopify-agency",
  },
};

export function detectLocale(pathname: string): Locale {
  return pathname === "/en" || pathname.startsWith("/en/") ? "en" : "de";
}

/** Resolve the equivalent path in the other locale (best-effort, falls back to home). */
export function alternateOf(pathname: string): { de: string; en: string } {
  for (const v of Object.values(pathMap)) {
    if (v.de === pathname || v.en === pathname) return v;
  }
  // Generic prefix-based fallback for routes not registered in pathMap
  // (e.g. dynamic detail routes, sub-pages added later).
  const prefixPairs: Array<[string, string]> = [
    ["/en/services", "/leistungen"],
    ["/en/regions", "/regionen"],
    ["/en/glossary", "/glossar"],
    ["/en/blog", "/blog"],
    ["/en/about", "/ueber-uns"],
    ["/en/contact", "/kontakt"],
    ["/en/imprint", "/impressum"],
    ["/en/privacy-policy", "/datenschutzerklaerung"],
    ["/en/accessibility", "/barrierefreiheit"],
    ["/en/tools", "/tools"],
  ];
  for (const [en, de] of prefixPairs) {
    if (pathname === en || pathname.startsWith(en + "/")) {
      return { en: pathname, de: de + pathname.slice(en.length) };
    }
    if (pathname === de || pathname.startsWith(de + "/")) {
      return { de: pathname, en: en + pathname.slice(de.length) };
    }
  }
  if (pathname === "/en" || pathname.startsWith("/en/")) {
    return { en: pathname, de: pathname === "/en" ? "/" : pathname.slice(3) };
  }
  return pathMap.home;
}

export const t = {
  de: {
    nav: { home: "Start", services: "Leistungen", tools: "Tools", about: "Über uns", contact: "Kontakt", glossary: "Glossar" },
    cta: "Kontakt aufnehmen",
    footer: {
      services: "Leistungen",
      company: "Unternehmen",
      legal: "Rechtliches",
      home: "Start",
      tagline: "Digitalagentur seit 2013.",
      city: "Karlstadt am Main.",
      glossary: "Glossar",
    },
  },
  en: {
    nav: { home: "Start", services: "Services", tools: "Tools", about: "About", contact: "Contact", glossary: "Glossary" },
    cta: "Get in touch",
    footer: {
      services: "Services",
      company: "Company",
      legal: "Legal",
      home: "Start",
      tagline: "Digital agency since 2013.",
      city: "Karlstadt am Main, Germany.",
      glossary: "Glossary",
    },
  },
} as const;
